import { makeLocalDateTime, moneyFromSmallestDenomination as money } from "@flockcover/flock-svc-core-types"
import { grossPremium, totalPayable } from "@flockcover/platform-types-common"
import { formatFloatTwoDecimals } from "@shared/utils/formatters/formatNumber"
import * as Platform from "@shared/platform-api/generated"
import makeUUID from "@shared/utils/helpers/makeUUID"
import { UUID } from "io-ts-types/lib/UUID"
import { cast } from "@flockcover/platform-types-utility"
import { AuthUserType } from "../../../auth/api/auth-service"
import { Connectivity, DistributorBrokerage, Policy, Pricing, Schedule, PremiumBand } from "./types"

export type User = Readonly<{
  type: AuthUserType | null
  viewerId: UUID | null
}>

export const createPolicyModern = (policy: Platform.Policy, user: User, stats: Platform.FleetStats): Policy => {
  const underwriter = policy.capacityProviders[0]

  return {
    id: policy.id as UUID,
    number: policy.number,
    numberOfInstallments: policy.meta.billingProcess.type === `distributed` ? 12 : 1,
    underwriter: { name: underwriter.formalName, link: underwriter.url },
    productType: policy.meta.productType === `usage_based` ? `UBI` : `FLAT_FEE`,
    startTime: makeLocalDateTime(policy.inceptionTime),
    endTime: makeLocalDateTime(policy.expiryTime),
    pricing: createPricing(policy, user),
    premiumBands: policy.effectiveSchedule.meta?.bands ? mapBands(policy, policy.effectiveSchedule.meta.bands) : [],
    holder: policy.insured.name,
    type: `MotorFlatFeePolicy`,
    currency: policy.currency,
    connectivity: makeConnectivity(policy, stats),
    // @TODO - this should be served from the back end
    // if its only usecase is to donwload fleet-schedule
    // as a CSV file.
    fleet: [],
    sector: mapSector(policy.meta.segment ?? null),
    excess: {
      value: policy?.meta?.adExcess ? [policy.meta.adExcess, 100] : [0, 1],
      currency: policy.currency,
    },
    effectiveSchedule: cast(Schedule)(policy.effectiveSchedule),
    schedules: [],
    meta: {
      billingProcess: policy.meta.billingProcess,
      productType: policy.meta.productType,
      vertical: policy.meta.vertical,
      modern: policy.meta.modern,
      ...(policy.meta.fleetId ? { fleetId: makeUUID(policy.meta.fleetId) } : {}),
    },
  }
}

const mapBands = (policy: Platform.Policy, bands: ReadonlyArray<Platform.ScheduleBand>): ReadonlyArray<PremiumBand> =>
  bands.map((band) => ({
    label: band.title,
    fixedGrossPremium: { value: [band.rate, 100], currency: policy.currency },
    // @TODO -  we dont support useage based yet...
    maxPerMileCost: { value: [0, 1], currency: policy.currency },
    minPerMileCost: { value: [0, 1], currency: policy.currency },
  }))

const mapSector = (segment: Platform.Segment["type"] | null) => {
  switch (segment) {
    case `courier`:
      return `Courier`
    case `self_drive_hire`:
      return `Self Drive Hire`
    case `subscription`:
      return `Subscription`
    case `taxi`:
      return `Taxi`
    case `trades`:
      return `Trades`
    default:
      // @TODO - Platform.Segment does not include this type..
      // but the legacy types expect it.
      // @TODO - add to type def on the platform.
      return `Food Distribution`
  }
}

const makeConnectivity = (policy: Platform.Policy, stats: Platform.FleetStats): Connectivity => ({
  onCoverTotal: stats.onCoverTotal,
  disconnectedTotal: Math.min(stats.onCoverTotal - stats.connectedVrns.length, 0),
  connectivityRate: formatFloatTwoDecimals(policy.connectivityRatio),
  connectedTotal: stats.connectedVrns.length,
})

const createPricing = (policy: Platform.Policy, user: User): Pricing => {
  const brokerages = createBrokerages(policy, user)
  const brokerage = brokerages.reduce((acc, cur) => acc + cur.brokerage.value[0], 0)
  const gross = grossPremium(policy.writtenPremium)
  const total = totalPayable(policy.writtenPremium)

  return {
    fees: money(policy.writtenPremium.fee, policy.currency),
    tax: money(policy.writtenPremium.tax, policy.currency),
    premium: money(gross - brokerage, policy.currency),
    totalPremium: money(total, policy.currency),
    brokerages,
  }
}

const createBrokerages = (policy: Platform.Policy, user: User): ReadonlyArray<DistributorBrokerage> =>
  policy.distributors
    .filter(
      (x) => user.type === `admin` || user.type === `claims_admin` || user.type === `carrier` || x.id === user.viewerId
    )
    .map((distributor) => {
      const gross = grossPremium(policy.writtenPremium)

      const brokerage = Math.floor(gross * distributor.split)

      return {
        brokerage: money(brokerage, policy.currency),
        distributor: distributor.name,
      }
    })
